package com.fireavert.react.shared.components.nav

import com.fireavert.cache.client.Preferences
import com.fireavert.menu.client.presentation.MenuController
import com.fireavert.menu.client.presentation.MenuViewModel
import com.fireavert.react.shared.styles.ModernNavStyles
import com.fireavert.utilities.getKoinInstance
import emotion.css.cx
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import web.window.WindowTarget
import web.window.window

sealed class NavOption {
    data object Administration : NavOption()
    data object Dashboard : NavOption()
    data object Properties : NavOption()
    data object Help : NavOption()
    data object Reports : NavOption()
    data object Feedback : NavOption()
    data object MyFireRisk : NavOption()
}

external interface SideNavProps : Props {
    var activeNavOption: NavOption
}


val SideNavV2 = FC<SideNavProps> { props ->
    val viewModel = getKoinInstance<MenuViewModel>()
    val controller = getKoinInstance<MenuController>()
    val preferences = getKoinInstance<Preferences>()

    var showAdministrationMenuItem by useState(false)
    var showClientsMenuItem by useState(false)
    var dashboardNumber by useState(0)
    var propertiesNumber by useState(0)
    var isCollapsed by useState(false)
    var propertyName by useState("Properties")
    var propertyMapState: Map<String, Int?> by useState(mapOf("All" to null))
    var defaultPropertyValueState by useState(preferences.globalProperty)

    var disableTimeSelector by useState(false)

    var propertyNameState by useState(
        preferences.globalProperty?.let { propId ->
            propertyMapState.entries.find { it.value == propId }?.key
        } ?: propertyMapState.entries.find { it.value == null }?.key ?: "Properties"
    )


    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            showAdministrationMenuItem = viewState.showAdministrationMenuItem
            showClientsMenuItem = viewState.showClientsMenuItem
            dashboardNumber = viewState.dashboardNumber
            propertiesNumber = viewState.propertiesNumber
            disableTimeSelector = viewState.disableTimeSelector

            if (propertyMapState != viewState.propertyMap && viewState.propertyMap.isNotEmpty()) {
                propertyMapState = viewState.propertyMap
            }

            //Check the initial state to decide if we need to disable the time selector
            controller.checkInitialPageState(window.location.pathname)
        }.launchIn(viewModel.scope)

        onCleanup {
            viewStateJob.cancel()
        }
        controller.didLoad()
    }

    useEffect(dependencies = arrayOf(preferences.globalProperty)) {
        if (defaultPropertyValueState != preferences.globalProperty) {
            defaultPropertyValueState = preferences.globalProperty
        }
    }

    useEffect(dependencies = arrayOf(defaultPropertyValueState, propertyMapState)) {
        val newName = if (defaultPropertyValueState == null) {
            "All" // Explicitly use "All" when property is null
        } else {
            propertyMapState.entries.find { it.value == defaultPropertyValueState }?.key
                ?: "All" // Default to "All" if not found
        }

        if (propertyNameState != newName) {
            propertyNameState = newName
        }
    }


    div {
        className = cx(
            ModernNavStyles.sideNav,
            if (isCollapsed) ModernNavStyles.sideNavCollapsed else null
        )
        ariaLabel = "SideNav"

        // Header section with logo and collapse button
        div {
            className = ModernNavStyles.sideNavHeader
            img {
                alt = "FireAvert Logo"
                src = "/static/logo_v2.svg"
                className = ModernNavStyles.logo
            }

//            button {
//                className = ModernNavStyles.collapseButton
//                onClick = { isCollapsed = !isCollapsed }
//                span {
//                    className = ModernNavStyles.collapseIcon
//                    +if (isCollapsed) "→" else "←"
//                }
//            }
        }

        // Global filters section
        GlobalFilters {
            coroutineScope = viewModel.scope
            this.controller = controller
            this.preferences = preferences
            this.propertyMapState = propertyMapState
            this.defaultPropertyValueState = defaultPropertyValueState
            this.isCollapsed = isCollapsed
            this.disableTimeSelector = disableTimeSelector
        }

        // Navigation sections
        div {
            className = ModernNavStyles.navSection

            // Main navigation group
            div {
                className = ModernNavStyles.navGroup
                if (!isCollapsed) {
                    div {
                        className = ModernNavStyles.navGroupLabel
                        +"MAIN"
                    }
                }

                ModernNavItem {
                    onClick = { controller.clickedDashboard() }
                    isActive = props.activeNavOption is NavOption.Dashboard
                    iconPath = "/static/dashboard_icon_20.svg"
                    text = "Dashboard"
                    this.isCollapsed = isCollapsed
                }

                ModernNavItem {
                    onClick = { controller.clickedProperties() }
                    isActive = props.activeNavOption is NavOption.Properties
                    iconPath = "/static/properties_icon_22.svg"
                    text = propertyName
                    badgeCount = propertiesNumber
                    this.isCollapsed = isCollapsed
                }

                ModernNavItem {
                    onClick = { controller.clickedReports() }
                    isActive = props.activeNavOption is NavOption.Reports
                    iconPath = "/static/reports_icon.svg"
                    text = "Reports"
                    this.isCollapsed = isCollapsed
                }

                ModernNavItem {
                    onClick = { controller.clickedMyFireRisk() }
                    isActive = props.activeNavOption is NavOption.MyFireRisk
                    iconPath = "/static/sidebar_icons/my_fire_risk_icon.svg"
                    text = "My Fire Risk"
                    badgeCount = null
                }

                if (showAdministrationMenuItem) {
                    ModernNavItem {
                        onClick = { controller.clickedAdministration() }
                        isActive = props.activeNavOption is NavOption.Administration
                        iconPath = "/static/report_icon.svg"
                        text = "Administration"
                        this.isCollapsed = isCollapsed
                    }
                }
            }

            div {
                className = ModernNavStyles.navGroup
                if (!isCollapsed) {
                    div {
                        className = ModernNavStyles.navGroupLabel
                        +"SUPPORT"
                    }
                }

                ModernNavItem {
                    onClick = { controller.clickedHelp() }
                    isActive = props.activeNavOption is NavOption.Help
                    iconPath = "/static/help_icon.svg"
                    text = "Help"
                    this.isCollapsed = isCollapsed
                }

                ModernNavItem {
                    onClick = { controller.clickedFeedback() }
                    isActive = props.activeNavOption is NavOption.Feedback
                    iconPath = "/static/sidebar_icons/feedback_icon.svg"
                    text = "Send Feedback"
                    this.isCollapsed = isCollapsed
                }
            }
        }

        div {
            className = ModernNavStyles.footerSection
            if (!isCollapsed) {
                a {
                    href = "https://fireavert.com/pages/privacy-policy"
                    target = WindowTarget._blank
                    className = ModernNavStyles.privacyPolicyLink
                    +"Privacy Policy"
                }
            }
        }
    }
}